import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Handshake, RefreshCw, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PdfViewerModal } from '../PdfViewerModal';
import { PROJECTS_API, authHeaders, L, STAGES, STAGE_STYLE, sortProjects } from './soldProjects';
import { SoldProjectRow } from './SoldProjectRow';
import { SoldProjectStageDialog } from './SoldProjectStageDialog';

// Card (Admin/GM): funil Contrato assinado → Pedido na fábrica → Pronto → Agendado → Instalado. embedded = sem minimizar (aba do Admin)
export const SoldProjectsCard = ({ language = 'pt-BR', refreshKey, onSchedule, embedded = false }) => {
  const t = L[language] || L['pt-BR'];
  const [collapsed, setCollapsed] = useState(() => !embedded && localStorage.getItem('sold_projects_collapsed') === '1');
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [dlg, setDlg] = useState(null); // {project, mode}
  const [pdf, setPdf] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${PROJECTS_API}?stage=all`, { headers: authHeaders() }); setItems(data || []); } catch (e) { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const toggle = () => { if (embedded) return; setCollapsed((c) => { localStorage.setItem('sold_projects_collapsed', c ? '0' : '1'); return !c; }); };

  const counts = useMemo(() => {
    const c = { open: 0, late: 0 };
    STAGES.forEach((s) => { c[s] = 0; });
    (items || []).forEach((p) => { c[p.stage] = (c[p.stage] || 0) + 1; if (p.stage !== 'installed') c.open += 1; if (p.late) c.late += 1; });
    return c;
  }, [items]);

  const list = useMemo(() => sortProjects((items || []).filter((p) => (filter === 'open' ? p.stage !== 'installed' : filter === 'late' ? p.late : filter === 'all' ? true : p.stage === filter)
    && (!q || `${p.customer_name} ${p.contract_number || ''} ${p.address || ''} ${p.city || ''} ${p.order_number || ''}`.toLowerCase().includes(q.toLowerCase())))), [items, filter, q]);

  const onSaved = (updated) => setItems((cur) => (cur || []).map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  const [notifying, setNotifying] = useState(null);
  const notifyCustomer = async (p) => {
    setNotifying(p.id);
    try {
      const { data } = await axios.post(`${PROJECTS_API}/${p.id}/notify-customer`, {}, { headers: authHeaders() });
      onSaved(data);
      toast.success(t.notifySent(data.customer_notified_to || p.customer_email));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.notifyError);
      load();
    } finally { setNotifying(null); }
  };
  const [sendingReview, setSendingReview] = useState(null);
  const sendReview = async (p) => {
    setSendingReview(p.id);
    try {
      const { data } = await axios.post(`${PROJECTS_API}/${p.id}/send-review`, {}, { headers: authHeaders() });
      onSaved(data);
      toast.success(t.reviewSentToast(data.review_email_to || p.customer_email));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.notifyError);
      load();
    } finally { setSendingReview(null); }
  };
  const openContract = (p) => setPdf({ src: `${PROJECTS_API}/${p.id}/contract-pdf?auth=${encodeURIComponent(sessionStorage.getItem('access_token') || '')}`, name: `${p.contract_number} - ${p.customer_name}.pdf` });

  const pills = [['open', t.open, counts.open], ...STAGES.map((s) => [s, t.short[s], counts[s]]), ['all', t.all, (items || []).length]];

  return (
    <div className={`bg-white border rounded-lg shadow-sm ${counts.late ? 'border-red-200' : counts.ready ? 'border-emerald-300' : 'border-teal-200'}`} data-testid="sold-projects-card">
      <div className="flex items-center gap-2 p-3 sm:p-4">
        <button type="button" onClick={toggle} className={`flex items-center gap-2 flex-1 text-left ${embedded ? 'cursor-default' : ''}`} data-testid="sold-projects-toggle">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-md"><Handshake className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" /></div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-zinc-900 tracking-tight">{t.title}</h3>
            {embedded && <p className="text-[11px] text-zinc-500 hidden sm:block">{t.hint}</p>}
          </div>
          {items && <Badge className="bg-teal-50 border border-teal-200 text-teal-700 text-xs" data-testid="sold-projects-count">{counts.open}</Badge>}
          {items && counts.ready > 0 && <Badge className="bg-emerald-100 border border-emerald-300 text-emerald-800 text-[10px] animate-pulse" data-testid="sold-projects-ready">{t.readyCount(counts.ready)}</Badge>}
          {items && counts.late > 0 && <Badge className="bg-red-100 border border-red-300 text-red-800 text-[10px]" data-testid="sold-projects-late">{t.lateCount(counts.late)}</Badge>}
        </button>
        <Button variant="outline" size="sm" className="h-7 w-7 p-0 border-zinc-300" onClick={load} title={t.refresh} data-testid="sold-projects-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        {!embedded && <button type="button" onClick={toggle} className="text-zinc-500 p-1" aria-label={collapsed ? 'expand' : 'collapse'} data-testid="sold-projects-chevron">{collapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}</button>}
      </div>
      {!collapsed && (
        <div className="px-3 pb-3 sm:px-4 sm:pb-4 space-y-2" data-testid="sold-projects-body">
          {items && items.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {pills.map(([k, label, n]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`sold-filter-${k}`}
                  className={`h-7 px-2.5 rounded-full border text-[11px] font-semibold inline-flex items-center gap-1 transition-colors ${filter === k ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'}`}>
                  {STAGE_STYLE[k] && <span className={`h-2 w-2 rounded-full ${STAGE_STYLE[k].dot}`} />}{label} <span className={filter === k ? 'text-white/70' : 'text-zinc-400'}>{n}</span>
                </button>
              ))}
              {counts.late > 0 && <button type="button" onClick={() => setFilter('late')} data-testid="sold-filter-late" className={`h-7 px-2.5 rounded-full border text-[11px] font-semibold ${filter === 'late' ? 'bg-red-600 text-white border-red-600' : 'bg-red-50 text-red-700 border-red-200'}`}>⚠ {t.onlyLate} {counts.late}</button>}
              {items.length > 3 && <div className="relative flex-1 min-w-[160px]"><Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-2" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.search} className="w-full h-7 rounded-md border border-zinc-200 pl-7 pr-2 text-xs" data-testid="sold-search" /></div>}
            </div>
          )}
          {items === null ? <p className="text-xs text-zinc-500">{t.loading}</p>
            : items.length === 0 ? <p className="text-xs text-zinc-500 py-4 text-center" data-testid="sold-projects-empty">{t.empty}</p>
            : list.length === 0 ? <p className="text-xs text-zinc-500 py-4 text-center" data-testid="sold-projects-empty-filter">{t.emptyFilter}</p>
            : <div className={`space-y-1.5 overflow-auto pr-0.5 ${embedded ? '' : 'max-h-[460px]'}`}>{list.map((p) => <SoldProjectRow key={p.id} p={p} t={t} lang={language} onStage={(project, mode) => setDlg({ project, mode })} onSchedule={onSchedule} onContract={openContract} onNotify={notifyCustomer} notifying={notifying === p.id} onSendReview={sendReview} sendingReview={sendingReview === p.id} />)}</div>}
        </div>
      )}
      {dlg && <SoldProjectStageDialog project={dlg.project} mode={dlg.mode} t={t} onClose={() => setDlg(null)} onSaved={onSaved} />}
      <PdfViewerModal open={Boolean(pdf)} onClose={() => setPdf(null)} src={pdf?.src} filename={pdf?.name} title={t.actions.contract} closeLabel="✕" testId="project-contract-pdf" />
    </div>
  );
};

export default SoldProjectsCard;
